// 买家端全局 Mock 状态层（购物车 / 订单 / 询盘）
// 全部内存态，刷新即重置（演示可接受，不持久化）。
import { createContext, useContext, useState, type ReactNode } from 'react';
import type { ContainerType, TradeTerm, PaymentMethod } from '@/mock/buyer/products';

export type InquiryStatus = 'pending' | 'quoted' | 'ordered';

export interface BuyerInquiry {
  id: string;
  productId: string;
  productName: string;
  containerType: ContainerType;
  targetTerm: TradeTerm;
  quantity: number;
  message: string;
  status: InquiryStatus;
  createdAt: string;
  quotePrice?: number;
}

export type OrderStatus = 'unpaid' | 'paid' | 'shipping' | 'done';

export interface BuyerOrder {
  id: string;
  productId: string;
  productName: string;
  containerType: ContainerType;
  tradeTerm: TradeTerm;
  payment: PaymentMethod;
  unitPrice: number;
  totalPrice: number;
  status: OrderStatus;
  createdAt: string;
}

interface AddInquiryInput {
  productId: string;
  productName: string;
  containerType: ContainerType;
  targetTerm: TradeTerm;
  quantity: number;
  message: string;
}

interface AddOrderInput {
  productId: string;
  productName: string;
  containerType: ContainerType;
  tradeTerm: TradeTerm;
  payment: PaymentMethod;
  unitPrice: number;
  totalPrice: number;
}

interface BuyerStoreValue {
  inquiries: BuyerInquiry[];
  orders: BuyerOrder[];
  addInquiry: (input: AddInquiryInput) => void;
  addOrder: (input: AddOrderInput) => BuyerOrder;
  /** 询盘「已报价」后接受报价并下单：标记询盘为已转订单（订单由下单页创建） */
  acceptInquiry: (id: string) => void;
}

const BuyerStoreContext = createContext<BuyerStoreValue | null>(null);

function genId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
}

export function BuyerStoreProvider({ children }: { children: ReactNode }) {
  const [inquiries, setInquiries] = useState<BuyerInquiry[]>([]);
  const [orders, setOrders] = useState<BuyerOrder[]>([]);

  const addInquiry = (input: AddInquiryInput) => {
    const inquiry: BuyerInquiry = {
      ...input,
      id: genId('inq'),
      status: 'pending',
      createdAt: new Date().toISOString(),
    };
    setInquiries((prev) => [inquiry, ...prev]);
  };

  const addOrder = (input: AddOrderInput): BuyerOrder => {
    const order: BuyerOrder = {
      ...input,
      id: genId('ord'),
      status: 'unpaid',
      createdAt: new Date().toISOString(),
    };
    setOrders((prev) => [order, ...prev]);
    return order;
  };

  const acceptInquiry = (id: string) => {
    setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, status: 'ordered' } : i)));
  };

  return (
    <BuyerStoreContext.Provider value={{ inquiries, orders, addInquiry, addOrder, acceptInquiry }}>
      {children}
    </BuyerStoreContext.Provider>
  );
}

export function useBuyerStore(): BuyerStoreValue {
  const ctx = useContext(BuyerStoreContext);
  if (!ctx) {
    throw new Error('useBuyerStore 必须在 BuyerStoreProvider 内使用');
  }
  return ctx;
}
